import axios, { InternalAxiosRequestConfig } from 'axios';

// ========================================
// UNIFIED API - NO VERSIONING
// All endpoints at /api/
// ========================================

// Create axios instance with configuration
// Use relative URL when accessing through proxy/production
// Use absolute URL only when on localhost (port 3000 or 3001)
const baseURL = window.location.hostname === 'localhost' && (window.location.port === '3001' || window.location.port === '3000')
  ? 'http://localhost:8001/api'
  : '/api';

// Log API configuration
console.log('%c[API CONFIG] Using Unified API', 'color: #0066ff; font-weight: bold');
console.log(`[API CONFIG] Base URL: ${baseURL}`);

export const api = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (Array.isArray(value)) {
          // Send arrays as multiple params with same key: status=ACTIVE&status=EXPIRED
          value.forEach(v => searchParams.append(key, v));
        } else if (value !== null && value !== undefined) {
          searchParams.append(key, value);
        }
      });
      return searchParams.toString();
    }
  }
});

// Request interceptor to add authentication
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Log only in development for debugging
    if (process.env.NODE_ENV === 'development' && config.url?.includes('cart')) {
      console.log('[CONFIG/API] Cart request:', `${config.baseURL}${config.url}`);
    }
    
    // Add authentication token
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for authentication handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - Token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Don't retry if this IS the refresh endpoint (prevent infinite loop)
      if (originalRequest.url?.includes('/auth/refresh')) {
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await api.post('/auth/refresh', {
            refresh_token: refreshToken,
          });

          localStorage.setItem('access_token', response.data.access_token);
          localStorage.setItem('refresh_token', response.data.refresh_token);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
          }
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear storage and redirect to login
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // Handle 403 Forbidden - Session expired
    if (error.response?.status === 403) {
      localStorage.clear();
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
      window.location.href = '/login?error=session_expired';
      return Promise.reject(new Error('Session expired. Please login again.'));
    }
    
    console.error('API Response Error:', error.response?.data || error.message);
    
    return Promise.reject(error);
  }
);

// Create a separate instance for public endpoints (no auth)
export const publicApi = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Export the configured instance
export default api;